/**
 * HealthCheckButton — Przycisk Health Check + wynik.
 *
 * Wyodrębnione z: /app/frontend/src/pages/settings/KsefSettingsPage.jsx
 * (linie 329-368 — handleHealthCheck;
 *  linie 869-915 — JSX wyniku Health Check w sekcji konfiguracji)
 *
 * Props:
 *   - onChecked (callback)  — wywoływane po check (do refresh diagnostyki)
 */
import { useState } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Activity, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { runHealthCheck } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const HealthCheckButton = ({ onChecked }) => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async () => {
    setChecking(true);
    setResult(null);

    try {
      const response = await runHealthCheck();
      setResult(response.data);

      if (response.data.status === "ok") {
        toast.success("Health check OK");
      } else {
        const reason = String(response.data.reason || response.data.message || "nieznany błąd");
        toast.error(`Health check: ${reason}`);
      }

      if (onChecked) onChecked();
    } catch (err) {
      logger.error("Error health check:", err);
      const errorMsg = safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd Health Check";
      setResult({
        status: "error",
        message: errorMsg,
        timestamp: new Date().toISOString(),
      });
      toast.error(errorMsg);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid="health-check-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-700">Health Check</p>
          <p className="text-xs text-gray-500">Weryfikuje certyfikat, klucz i połączenie z KSeF</p>
        </div>
        <Button
          onClick={handleCheck}
          disabled={checking}
          variant="outline"
          size="sm"
          data-testid="health-check-btn"
        >
          {checking ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              Sprawdzam...
            </>
          ) : (
            <>
              <Activity className="w-4 h-4 mr-2" />
              Uruchom Health Check
            </>
          )}
        </Button>
      </div>

      {result && (
        <div className={`mt-3 p-3 rounded-lg text-sm ${
          result.status === "ok" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
        }`}>
          <div className="flex items-start gap-2">
            {result.status === "ok" ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={result.status === "ok" ? "text-green-800 font-medium" : "text-red-800 font-medium"}>
                {result.status === "ok" ? "Połączenie OK" : `Błąd: ${String(result.reason || result.message || "nieznany")}`}
              </p>
              {result.detail && (
                <p className="text-xs mt-1 text-gray-600">{String(result.detail)}</p>
              )}
              {result.timestamp && (
                <p className="text-xs mt-2 text-gray-500">
                  Sprawdzono: {new Date(result.timestamp).toLocaleString("pl-PL")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthCheckButton;
